import { FC, useState, MouseEvent } from 'react';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import ViewRawMaterialDialog from 'src/components/DialogBox/ViewRawMaterialDialog/ViewRawMaterialDialog';
import {
  actionButtons,
  apiUrls,
  productStatus,
  rawMaterialDialogTitles,
  rawMaterialDialogSubtitles,
  productDialogTitles,
  productDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { RawMaterial } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import {
  rawMaterialInitialValues,
  resetQuantityInitialValues,
} from 'src/utils/initialValues.utils';
import {
  rawMaterialValidationSchema,
  resetQuantityValidation,
} from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import UnitField from 'src/components/InputFields/UnitPicker';
import CategoryPicker from 'src/components/InputFields/CategoryPicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import {
  FlexColumnBox,
  FlexContainerBox,
  FlexRowStartBox,
  ResetFlexRowBox,
} from '../DialogBox/POBox/POBox.styles';
import { fetchRawMaterialUnits } from 'src/services/enums.service';
import { useGetBasicRawMaterialCategories } from 'src/services/rawMaterialCategory.service';

const RawMaterialActionMenuButton: FC<{
  rowId: any;
  code: string;
  status: string;
  availableQty: number | string;
  fetchMaterials: () => void;
  deleteMaterial: (id: string) => void;
}> = ({
  rowId,
  code,
  status,
  availableQty,
  fetchMaterials,
  deleteMaterial,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<RawMaterial | null>(
    null
  );
  const [unitList, setUnitList] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { basicCategories, fetchBasicCategories } =
    useGetBasicRawMaterialCategories();

  const fetchUnits = async () => {
    try {
      const response = await fetchRawMaterialUnits();
      setUnitList(response);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditFormSubmit = async () => {
    if (!selectedMaterial) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_RAW_MATERIAL, {
        id: rowId,
        code: formik.values.code,
        name: formik.values.name,
        unit: formik.values.unit,
        unitPrice: formik.values.unitPrice,
        minLevelQty: formik.values.minLevelQty,
        rawMaterialCategoryId: formik.values.rawMaterialCategoryId || null,
      });
      setEditDialogOpen(false);
      fetchMaterials();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (material: RawMaterial) => {
    setSelectedMaterial(material);

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_MATERIAL_INFO_BY_ID,
        { params: { id: material.id } }
      );

      formik.setValues({
        code: material.code,
        name: response.data.name,
        unit: response.data.unit,
        unitPrice: Number(response.data.unitPrice).toFixed(2),
        minLevelQty: response.data.minLevelQty,
        rawMaterialCategoryId: response.data.rawMaterialCategoryId,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedMaterial(null);
    formik.resetForm();
  };

  const handleResetFormSubmit = async () => {
    try {
      const response = await axiosTokenInstance.post(
        apiUrls.RESET_RAW_MATERIAL_QUANTITY,
        {
          rawMaterialId: rowId,
          quantity: Number(resetFormik.values.quantity),
        }
      );
      setResetDialogOpen(false);
      resetFormik.resetForm();
      fetchMaterials();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleResetDialogOpen = () => {
    handleClose();
    resetFormik.resetForm();
    resetFormik.setFieldValue('quantity', availableQty);
    setResetDialogOpen(true);
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
    resetFormik.resetForm();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    fetchUnits();
    fetchBasicCategories();
    setAnchorEl(event.currentTarget);
  };

  const handleView = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_MATERIAL_INFO_BY_ID,
        { params: { id: rowId } }
      );
      setSelectedMaterial(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedMaterial(null);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setSelectedCode(code);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedCode) return;

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId) {
          await deleteMaterial(rowId);
        }
      } else if (
        dialogType === actionButtons.ACTIVATE ||
        dialogType === actionButtons.DEACTIVATE
      ) {
        const response = await axiosTokenInstance.patch(
          apiUrls.RAW_MATERIAL_ACTIVATION,
          { id: rowId, isActive: dialogType === actionButtons.ACTIVATE }
        );
        showSnackbar(response.data.message, 'success');
      }
      fetchMaterials();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedCode(null);
    }
  };

  const formik = useFormik({
    initialValues: rawMaterialInitialValues,
    validationSchema: rawMaterialValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const resetFormik = useFormik({
    initialValues: resetQuantityInitialValues,
    validationSchema: resetQuantityValidation,
    onSubmit: handleResetFormSubmit,
  });

  const getDialogHeight = () => {
    const fieldGroups = [
      ['name', 'code'],
      ['unit', 'unitPrice'],
      ['availableQty', 'minLevelQty'],
    ];
    return calculateDialogHeight(formik, fieldGroups);
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleView}
            iconClassName="bx bx-file-find"
            text="View"
          />
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                code,
                name: '',
                unit: '',
                unitPrice: '',
                availableQty: 0,
                minLevelQty: 0,
                isActive: status === productStatus.ACTIVE,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={rawMaterialDialogTitles.EDIT}
            subtitle={rawMaterialDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedMaterial && (
              <FlexContainerBox>
                <FlexRowStartBox>
                  <FlexColumnBox>
                    <TextField
                      id="name"
                      label="Name"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>

                  <FlexColumnBox>
                    <TextField
                      id="code"
                      label="Code"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>

                <FlexRowStartBox>
                  <FlexColumnBox>
                    <UnitField
                      id="unit"
                      label="Unit"
                      formikProps={formik}
                      unitList={unitList}
                      width="294px"
                    />
                  </FlexColumnBox>

                  <FlexColumnBox>
                    <TextField
                      id="unitPrice"
                      placeholder="00.00"
                      label="Unit Price"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>

                <FlexRowStartBox>
                  <FlexColumnBox>
                    <TextField
                      id="minLevelQty"
                      label="Minimum Quantity Level"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>

                  <FlexColumnBox>
                    <CategoryPicker
                      id="rawMaterialCategoryId"
                      label="Category"
                      categories={basicCategories}
                      value={formik.values.rawMaterialCategoryId || ''}
                      onChange={(value) =>
                        formik.setFieldValue('rawMaterialCategoryId', value)
                      }
                      width="294px"
                      allowNone={true}
                      noneLabel="None"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>
              </FlexContainerBox>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() =>
              handleDialogOpen(
                status === productStatus.ACTIVE
                  ? actionButtons.DEACTIVATE
                  : actionButtons.ACTIVATE
              )
            }
            iconClassName={
              status === productStatus.ACTIVE
                ? 'bx bx-minus-circle'
                : 'bx bx-radio-circle-marked'
            }
            text={status === productStatus.ACTIVE ? 'Deactivate' : 'Activate'}
          />
          <MenuItemWithIcon
            onClick={handleResetDialogOpen}
            iconClassName="bx bx-reset"
            text="Reset Quantity"
          />
          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        paddingRight="60px"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? rawMaterialDialogTitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? rawMaterialDialogTitles.ACTIVATE
            : rawMaterialDialogTitles.DEACTIVATE
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? rawMaterialDialogSubtitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? rawMaterialDialogSubtitles.ACTIVATE
            : rawMaterialDialogSubtitles.DEACTIVATE
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--warning)'
            : 'var(--error)'
        }
      />

      <ViewRawMaterialDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        data={selectedMaterial}
      />

      <CustomDialog
        height="350px"
        width="650px"
        open={resetDialogOpen}
        handleClose={handleResetDialogClose}
        title={productDialogTitles.RESET}
        subtitle={productDialogSubtitles.RESET}
        onSubmit={resetFormik.handleSubmit}
        smScreen={false}
        buttonLabel="Reset"
        showCancelButton={true}
      >
        <FlexContainerBox>
          <ResetFlexRowBox>
            <FlexColumnBox>
              <TextField
                id="quantity"
                label="Available Quantity"
                formikProps={resetFormik}
                width="294px"
              />
            </FlexColumnBox>
          </ResetFlexRowBox>
        </FlexContainerBox>
      </CustomDialog>
    </>
  );
};
export default RawMaterialActionMenuButton;
